import type { ColdMonitoringCallContext } from 'shared'
import type { ContextHydrationTarget } from './types'

/**
 * Replays a cold-monitoring snapshot into a livekit-compatible target.
 *
 * Applies call id, history, flag deltas, and lifecycle events in the same order
 * as the UI hydration flow. This function does not reset existing state first,
 * so callers must hydrate into a fresh or already reset target.
 */
export const hydrateContextSnapshot = (
  target: ContextHydrationTarget,
  context: ColdMonitoringCallContext | null | undefined,
) => {
  if (!context) return

  // Set call ID if present
  if (context.call_id) {
    target.setCallId(context.call_id)
  }

  // Hydrate history
  if (context.history?.length) {
    target.addContextHistory(context.history)
  }

  // Hydrate flags map from snapshot
  if (context.flags) {
    for (const [segmentId, flags] of Object.entries(context.flags)) {
      for (const flag of flags) {
        target.mergeFlagDelta({ segment_id: segmentId, flags: [flag] })
      }
    }
  }

  // Hydrate lifecycle events from shapshot
  if (context.lifecycle_events) {
    for (const event of context.lifecycle_events) {
      target.addLifecycleEvent(event)
    }
  }

  // Hydrate persisted ARBEO latency readings as well. Init and auto-chained
  // tools may not have a matching tool_result in history, so the engine handles
  // those as standalone events and enriches matching tool calls when possible.
  for (const event of context.tool_latencies ?? []) {
    target.addToolLatencyEvent(event.tool, event.latency_ms, event.timestamp)
  }
}
